import { z } from 'zod';

export const SimulationSettingEnum = {
  // SMS Connection
  SMS_CONNECTION: 'sms_connection',
  // Contact Center
  CONTACT_CENTER: 'contact_center',
  // Code Verification
  CODE_VERIFICATION: 'code_verification',
  // Contact Creation
  CONTACT_CREATION: 'contact_creation',
  // Application Submission
  APPLICATION_SUBMISSION: 'application_submission',
  // Call Connection
  CALL_CONNECTION: 'call_connection',
  // Experian Verification
  EXPERIAN_VERIFICATION: 'experian_verification',
  // Success Step Conditions
  TCPA_HOURS: 'tcpa_hours',
  WEEKEND: 'weekend',
  HOLIDAY: 'holiday',
  BPO_AGENT: 'bpo_agent',
  CALL_INITIATED: 'call_initiated',
  SCHEDULED_TIME: 'scheduled_time',
  SHOULD_SEND_CALL: 'should_send_call',
  // Prospect Verification
  PROSPECT_VERIFY: 'prospect_verify',
  // Manual Lead
  MANUAL_LEAD: 'manual_lead',
  // KBA Connect
  KBA_CONNECT: 'kba_connect',
  // KBA Verify
  KBA_VERIFY: 'kba_verify',
  // A/B Testing
  AB_TEST_GROUP: 'ab_test_group',
  // Lead details deep-link (useruniqueId)
  LEAD_DETAILS: 'lead_details'
} as const;

export type SimulationSetting = typeof SimulationSettingEnum[keyof typeof SimulationSettingEnum];

export const simulationSettingsSchema = z.object({
  // SMS Connection Response
  [SimulationSettingEnum.SMS_CONNECTION]: z.enum([
    'success',
    'user_exists',
    'invalid_phone',
    'service_unavailable',
    'error_500'
  ]),
  
  // Contact Center Availability
  [SimulationSettingEnum.CONTACT_CENTER]: z.enum([
    'available',
    'unavailable',
    'partial'
  ]),
  
  // Code Verification Response
  [SimulationSettingEnum.CODE_VERIFICATION]: z.enum([
    'success',
    'invalid_code',
    'expired_code',
    'too_many_attempts',
    'error_500'
  ]),
  
  // Contact Creation Response
  [SimulationSettingEnum.CONTACT_CREATION]: z.enum([
    'qualified',
    'qualified_tier2',
    'rejected',
    'error',
    'error_400',
    'error_500',
    'error_503'
  ]),
  
  // Application Submission Response
  [SimulationSettingEnum.APPLICATION_SUBMISSION]: z.enum([
    'success',
    'rejected',
    'system_error'
  ]),

  // Call Connection Response
  [SimulationSettingEnum.CALL_CONNECTION]: z.enum([
    'success',
    'invalid_record',
    'server_error'
  ]),

  // Experian Verification Response
  [SimulationSettingEnum.EXPERIAN_VERIFICATION]: z.enum([
    'qualified',        // 202 – accepted, QUALIFIED → Success
    'qualified_tier2',  // 202 – accepted, QUALIFIED (debt2 product) → Success
    'rejected',         // 422 – unqualified (state=rejected) → Unsuccessful
    'validation_error', // 400 – missing/invalid required field → Manual Lead Fallback
    'error_500',        // 500 – server error → Manual Lead Fallback
    'error_503'         // 503 – upstream unavailable → Manual Lead Fallback
  ]),

  // Success Step - TCPA Hours
  [SimulationSettingEnum.TCPA_HOURS]: z.enum([
    'within',
    'outside',
    'auto'
  ]),

  // Success Step - Weekend
  [SimulationSettingEnum.WEEKEND]: z.enum([
    'yes',
    'no',
    'auto'
  ]),

  // Success Step - Holiday
  [SimulationSettingEnum.HOLIDAY]: z.enum([
    'yes',
    'no',
    'auto'
  ]),

  // Success Step - BPO Agent
  [SimulationSettingEnum.BPO_AGENT]: z.enum([
    'yes',
    'no',
    'auto'
  ]),

  // Success Step - Call Initiated
  [SimulationSettingEnum.CALL_INITIATED]: z.enum([
    'yes',
    'no',
    'auto'
  ]),

  // Success Step - Scheduled Time
  [SimulationSettingEnum.SCHEDULED_TIME]: z.enum([
    'set',
    'unset',
    'auto'
  ]),

  // Success Step - Should Send Call (auto-dialer eligibility)
  [SimulationSettingEnum.SHOULD_SEND_CALL]: z.enum([
    'yes',
    'no',
    'auto'
  ]),

  // Prospect Verification Response
  [SimulationSettingEnum.PROSPECT_VERIFY]: z.enum([
    'qualified',        // 200 – QUALIFIED → proceed to Experian
    'unqualified',      // 200 – UNQUALIFIED → Unsuccessful
    'validation_error', // 400 – invalid/missing offerCode or personal info → Unsuccessful
    'error_500'         // 500 – unexpected server error → Unsuccessful
  ]),

  // Manual Lead Response
  [SimulationSettingEnum.MANUAL_LEAD]: z.enum([
    'qualified',        // 200 – QUALIFIED → Success
    'unqualified',      // 200 – UNQUALIFIED → Unsuccessful
    'validation_error', // 400 – missing/invalid required field → Retry / Unsuccessful after 3x
    'not_found'         // 404 – HubSpot contact not found → Unsuccessful
  ]),

  // KBA Connect Response
  [SimulationSettingEnum.KBA_CONNECT]: z.enum([
    'success',
    'error_500'
  ]),

  // KBA Verify Response
  [SimulationSettingEnum.KBA_VERIFY]: z.enum([
    'success',
    'in_progress',
    'error_500'
  ]),

  // A/B Test Group
  [SimulationSettingEnum.AB_TEST_GROUP]: z.enum([
    'A',
    'B',
    'C',
    'D',
    'auto'
  ]),

  // Lead details deep-link (useruniqueId → GET /leads/:id/details)
  [SimulationSettingEnum.LEAD_DETAILS]: z.enum([
    'accepted',
    'accepted_tier2',
    'rejected',
    'error'
  ])
});

export type SimulationSettings = z.infer<typeof simulationSettingsSchema>;

export const defaultSimulationSettings: SimulationSettings = {
  [SimulationSettingEnum.SMS_CONNECTION]: 'success',
  [SimulationSettingEnum.CONTACT_CENTER]: 'available',
  [SimulationSettingEnum.CODE_VERIFICATION]: 'success',
  [SimulationSettingEnum.CONTACT_CREATION]: 'qualified',
  [SimulationSettingEnum.APPLICATION_SUBMISSION]: 'success',
  [SimulationSettingEnum.CALL_CONNECTION]: 'success',
  [SimulationSettingEnum.EXPERIAN_VERIFICATION]: 'qualified',
  [SimulationSettingEnum.TCPA_HOURS]: 'auto',
  [SimulationSettingEnum.WEEKEND]: 'auto',
  [SimulationSettingEnum.HOLIDAY]: 'auto',
  [SimulationSettingEnum.BPO_AGENT]: 'auto',
  [SimulationSettingEnum.CALL_INITIATED]: 'auto',
  [SimulationSettingEnum.SCHEDULED_TIME]: 'auto',
  [SimulationSettingEnum.SHOULD_SEND_CALL]: 'auto',
  [SimulationSettingEnum.PROSPECT_VERIFY]: 'qualified',
  [SimulationSettingEnum.MANUAL_LEAD]: 'qualified',
  [SimulationSettingEnum.KBA_CONNECT]: 'success',
  [SimulationSettingEnum.KBA_VERIFY]: 'success',
  [SimulationSettingEnum.AB_TEST_GROUP]: 'auto',
  [SimulationSettingEnum.LEAD_DETAILS]: 'accepted'
};